import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Validates that an ID is a valid, non-empty string that is not the literal "undefined".
 * Used as a guard for all database queries to prevent 400 Bad Request errors.
 */
export function isValidId(id: string | undefined | null): id is string {
  return Boolean(id && typeof id === 'string' && id !== 'undefined' && id !== 'null' && id.trim() !== '');
}

/**
 * Validates that a value is a valid UUID v4 format.
 */
export function isUUID(value: string | undefined | null): boolean {
  if (!value || typeof value !== 'string') return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
}

/**
 * Default NCAA revenue-sharing budget cap ($20.5M).
 * Used as fallback for percentage calculations to prevent division by zero.
 */
export const DEFAULT_BUDGET_CAP = 20500000;

/**
 * Safely calculates a percentage, returning 0 if the denominator is invalid.
 */
export function safePercentage(numerator: number, denominator: number | undefined | null): number {
  const safeDenominator = denominator && denominator > 0 ? denominator : DEFAULT_BUDGET_CAP;
  return (numerator / safeDenominator) * 100;
}

/**
 * Formats a number as currency (USD).
 */
export function formatCurrency(amount: number | undefined | null): string {
  if (amount === undefined || amount === null) return '$0';
  
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`;
  }
  return `$${amount.toLocaleString()}`;
}
